import { getToken, logout } from './auth';

const API_BASE = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5001/api' : '/api');

function authHeaders() {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function handleResponse(res) {
  if (res.status === 401) {
    logout();
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  if (res.status === 429) {
    throw new Error('Too many requests — please try again later');
  }
  if (!res.ok) {
    let msg = `API error: ${res.status}`;
    try { const body = await res.json(); if (body?.error) msg = body.error; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

async function fetchJSON(url) {
  const res = await fetch(`${API_BASE}${url}`, { headers: authHeaders() });
  return handleResponse(res);
}

async function postJSON(url, body) {
  const res = await fetch(`${API_BASE}${url}`, {
    method: 'POST',
    headers: authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  return handleResponse(res);
}

async function patchJSON(url, body) {
  const res = await fetch(`${API_BASE}${url}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body) });
  return handleResponse(res);
}

async function deleteReq(url) {
  const res = await fetch(`${API_BASE}${url}`, { method: 'DELETE', headers: authHeaders() });
  return handleResponse(res);
}

export const api = {
  // Gold prices
  getGoldHistory: (days = 30) => fetchJSON(`/gold/history?days=${days}`),


  // Predictions
  getPredictionTodayAll: () => fetchJSON('/prediction/today/all'),
  getPredictionByDate: (date) => fetchJSON(`/prediction/date/${date}`),
  getPredictionAllByDate: (date) => fetchJSON(`/prediction/date/${date}/all`),
  getPredictionHistory: (limit = 30) => fetchJSON(`/prediction/history?limit=${limit}`).then(r => r.data ?? r),
  getAccuracy: () => fetchJSON('/prediction/accuracy'),
  getMasterAccuracy: () => fetchJSON('/prediction/master-accuracy'),
  getSizing: (capital = 10000, kellyMultiplier = 0.5) =>
    fetchJSON(`/prediction/sizing?capital=${capital}&kellyMultiplier=${kellyMultiplier}`),

  // Analytics
  getAnalyticsByDate: (date) => fetchJSON(`/analytics/date/${date}`),
  getAnalyticsHistory: (limit = 30) => fetchJSON(`/analytics/history?limit=${limit}`).then(r => r.data ?? r),

  // Data management
  getGoldStats: () => fetchJSON('/gold/stats'),
  getAIStatus: (date = null) => fetchJSON(date ? `/engines/ai-status?date=${date}` : '/engines/ai-status'),
  getCronLogs: (limit = 20) => fetchJSON(`/gold/cron-logs?limit=${limit}`),
  getPipelineStatus: () => fetchJSON('/gold/pipeline-status'),
  getCronHealth: () => fetchJSON('/system/cron-health'),
  getAnomalyLog: () => fetchJSON('/system/anomaly-log'),
  getEngineLeaderboard: () => fetchJSON('/system/engine-leaderboard'),
  runFullCycle: () => postJSON('/gold/run-cycle'),
  resetAllData: (pin) => postJSON('/gold/reset', { pin }),
  getAuditLogs: (limit = 50) => fetchJSON(`/gold/audit-logs?limit=${limit}`),

  // Engine Performance
  getEngineRankings: (workspace) => fetchJSON(`/engines/rankings${workspace ? `?workspace=${workspace}` : ''}`),
  getEngineScores: (date, workspace) => {
    const ws = workspace ? `?workspace=${workspace}` : '';
    return date ? fetchJSON(`/engines/scores/${date}${ws}`) : fetchJSON(`/engines/scores${ws}`);
  },
  getEngineHistory: (engine, limit = 30) => fetchJSON(`/engines/history/${engine}?limit=${limit}`),
  getEngineRanking: (engine) => fetchJSON(`/engines/ranking/${engine}`),
  getEngineGradeMonthly: (engine) => fetchJSON(`/engines/grade-monthly/${engine}`),
  getEngineGradeDist: () => fetchJSON('/engines/grade-dist'),
  getEngineDates: () => fetchJSON('/engines/dates'),
  getXGrades: (limit = 500, workspace) => fetchJSON(`/engines/x-grades?limit=${limit}${workspace ? `&workspace=${workspace}` : ''}`),
  runScoring: () => postJSON('/engines/score'),
  previewMasterSignal: () => postJSON('/engines/preview-master', {}),
  recalculateGrades: () => postJSON('/engines/recalculate-grades'),
  getJobStatus: () => fetchJSON('/engines/job-status'),
  resetJob: (job) => postJSON('/engines/reset-job', { job }),
  // Returns plain engine array (backwards-compatible for existing callers)
  getEngineList: () => fetchJSON('/engines/list').then(r => r.engines ?? r),
  // Returns { engines, count, categories } — use this for dynamic counts/filters
  getEnginesMeta: () => fetchJSON('/engines/list'),
  getEngineConfig: () => fetchJSON('/engines/config'),
  setEngineConfig: (engine, enabled) => postJSON('/engines/config', { engine, enabled }),
  setEngineConfigBulk: (engines) => postJSON('/engines/config/bulk', { engines }),
  // Admin Engine Control — workspace-aware overview + settings upsert.
  // workspace: 'retail' | 'remit' | 'trade' | 'depart'
  getEngineOverview: (workspace = 'retail') => fetchJSON(`/engines/overview?workspace=${encodeURIComponent(workspace)}`),
  setEngineSettings: ({ workspace, engine, enabled, weight, confidence_cap, regime_pref, notes, params }) =>
    postJSON('/engines/settings', { workspace, engine, enabled, weight, confidence_cap, regime_pref, notes, params }),
  runEngineNow: (engine) => postJSON(`/engines/run-now/${encodeURIComponent(engine)}`),
  getEngineManifest: (engine) => fetchJSON(`/engines/manifest/${encodeURIComponent(engine)}`),
  getEngineAudit: (engine, days = 90) => fetchJSON(`/engines/audit/${encodeURIComponent(engine)}?days=${days}`),

  // Simulator
  getLivePrice: (label, group) => fetchJSON(`/gold/live-price?label=${encodeURIComponent(label)}&group=${encodeURIComponent(group)}`),
  runSimulation: (todayPrice, priceHistory) => postJSON('/gold/simulate', { todayPrice, priceHistory }),
  getSimulationStatus: (jobId) => fetchJSON(`/gold/simulate/status/${jobId}`),

  // Remit — USD/INR "send money home today, or wait?" signal
  getRemitToday: () => fetchJSON('/remit/today'),
  getRemitHistory: (limit = 30) => fetchJSON(`/remit/history?limit=${limit}`),
  getRemitAccuracy: () => fetchJSON('/remit/accuracy'),
  runRemit: () => postJSON('/remit/run', {}),
  backfillRemit: (days = 120) => postJSON('/remit/backfill', { days }),
  getRemitHealth: () => fetchJSON('/remit/health'),
  clearRemitPredictions: () => deleteReq('/remit/predictions'),
  getRemitCorridor: () => fetchJSON('/remit/corridor'),
  updateRemitCorridor: (corridor) => postJSON('/remit/corridor', corridor),

  // Depart — flight fare "book now, or wait?" signal. Reads take an optional
  // route id; omitting it falls back to the first active route server-side.
  getDepartRoutes: () => fetchJSON('/depart/routes'),
  // Track any city pair. The route is created if new, reused if someone already
  // follows it — so a second follower inherits the existing price history.
  trackDepartRoute: ({ origin, destination, currency = 'BHD', horizonDays = 30, targetFare = null }) =>
    postJSON('/depart/routes', { origin, destination, currency, horizon_days: horizonDays, target_fare: targetFare }),
  getDepartToday: (route) => fetchJSON(`/depart/today${route ? `?route=${route}` : ''}`),
  getDepartHistory: (route, limit = 60) => fetchJSON(`/depart/history?limit=${limit}${route ? `&route=${route}` : ''}`),
  getDepartSeries: (route, limit = 90) => fetchJSON(`/depart/series?limit=${limit}${route ? `&route=${route}` : ''}`),
  getDepartAccuracy: (route) => fetchJSON(`/depart/accuracy${route ? `?route=${route}` : ''}`),
  getDepartCollector: () => fetchJSON('/depart/collector'),
  getDepartSubscriptions: () => fetchJSON('/depart/subscriptions'),
  subscribeDepart: (routeId, targetFare = null) => postJSON('/depart/subscriptions', { route_id: routeId, target_fare: targetFare }),
  unsubscribeDepart: (routeId) => deleteReq(`/depart/subscriptions/${routeId}`),
  runDepart: () => postJSON('/depart/run', {}),
  // One route per call — a six-route replay cannot return inside the HTTP
  // timeout, so the Hindsight tab loops routes and calls this per route.
  backfillDepart: (days = 120, routeId = null) => postJSON('/depart/backfill', { days, route_id: routeId }),
  getDepartHealth: () => fetchJSON('/depart/health'),
  clearDepartPredictions: () => deleteReq('/depart/predictions'),

  // ML Lab — model registry, conformal intervals, bandit shadow, anomalies
  getMlOverview: () => fetchJSON('/ml/overview'),
  runMlRetrain: () => postJSON('/ml/retrain', {}),
  runMlDaily: () => postJSON('/ml/run-daily', {}),

  // Trade workspace — broker feed, risk defaults, per-user risk overrides.
  // Backend storage is in-memory today; payload shapes match the frontend's
  // localStorage shapes so swapping persistence layer is a one-line change.
  getTradeFeed:          ()              => fetchJSON('/trade/feed'),
  saveTradeFeed:         (data)          => postJSON('/trade/feed', data),
  testTradeFeed:         (data)          => postJSON('/trade/feed/test', data),
  getMt5Latest:          ()              => fetchJSON('/trade/mt5/latest'),
  getMt5Candles:         (tf = 'M5', limit = 60) => fetchJSON(`/trade/mt5/candles?tf=${tf}&limit=${limit}`),
  getMt5Status:          ()              => fetchJSON('/trade/mt5/status'),
  getTradeRiskDefaults:  ()              => fetchJSON('/trade/risk/defaults'),
  saveTradeRiskDefaults: (data)          => postJSON('/trade/risk/defaults', data),
  getUserTradeRisk:      (userId)        => fetchJSON(`/trade/risk/user/${userId}`),
  saveUserTradeRisk:     (userId, data)  => patchJSON(`/trade/risk/user/${userId}`, data),

  // Integrations (AI services, API keys)
  getIntegrations: () => fetchJSON('/integrations'),
  saveIntegration: (data) => postJSON('/integrations', data),
  testIntegration: (provider) => postJSON(`/integrations/${provider}/test`),
  deleteIntegration: (provider) => deleteReq(`/integrations/${provider}`),

  // Alerts
  getAlerts: () => fetchJSON('/alerts'),
  saveAlerts: (data) => postJSON('/alerts', data),
  testEmail: () => postJSON('/alerts/test-email'),
  testTelegram: () => postJSON('/alerts/test-telegram'),

  // Engine Validator
  runEngineTests: () => postJSON('/system/engine-test'),

  // Market Data Config
  getMarketConfig: () => fetchJSON('/market/config'),
  updateMarketConfig: (indicator, data) => postJSON(`/market/config/${indicator}`, data),
  testMarketSource: (indicator) => postJSON(`/market/config/${indicator}/test`),
  saveMarketValue: (indicator, value) => postJSON(`/market/config/${indicator}/save`, { value }),
  syncAllMarket: () => postJSON('/market/sync'),

  // Claude Co-worker MCP API key
  getMcpToken: () => fetchJSON('/mcp/token'),
  regenerateMcpToken: () => postJSON('/mcp/token/regenerate'),

  // OAuth connected clients (MCP connector revocation UI)
  // /oauth/* is mounted at the app root, not under /api.
  listOAuthClients: async () => {
    const base = API_BASE.endsWith('/api') ? API_BASE.slice(0, -4) : API_BASE.replace(/\/api$/, '');
    const res = await fetch(`${base}/oauth/clients`, { headers: authHeaders() });
    return handleResponse(res);
  },
  revokeOAuthClient: async (clientId) => {
    const base = API_BASE.endsWith('/api') ? API_BASE.slice(0, -4) : API_BASE.replace(/\/api$/, '');
    const res = await fetch(`${base}/oauth/clients/${encodeURIComponent(clientId)}`, { method: 'DELETE', headers: authHeaders() });
    return handleResponse(res);
  },

  // Web Push (PWA)
  getVapidPublicKey: () => fetchJSON('/alerts/vapid-public-key'),
  pushSubscribe: (subscription) => postJSON('/alerts/push-subscribe', subscription),
  pushUnsubscribe: (endpoint) => postJSON('/alerts/push-unsubscribe', { endpoint }),
  testPush: () => postJSON('/alerts/test-push'),

  // Backtest
  listBacktestRuns: () => fetchJSON('/backtest/runs'),
  getBacktestRun: (id) => fetchJSON(`/backtest/runs/${id}`),
  startBacktest: (days) => postJSON('/backtest/run', { days }),
  deleteBacktestRun: (id) => deleteReq(`/backtest/runs/${id}`),
  cancelBacktestRun: (id) => postJSON(`/backtest/runs/${id}/cancel`),

  // User management (admin-only)
  listUsers:   () => fetchJSON('/users'),
  createUser:  (body) => postJSON('/users', body),       // { username, password, role }
  updateUser:  (id, body) => patchJSON(`/users/${id}`, body), // { role?, password? }
  deleteUser:  (id) => deleteReq(`/users/${id}`),



  // Backfill (historical replay; standalone module)
  getBackfillConfig:        () => fetchJSON('/backfill/config'),
  testBackfillSources:      () => fetchJSON('/backfill/test-sources'),
  getMarketDataCoverage:    (from, to) => fetchJSON(`/backfill/market-data-coverage?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  fillMarketData:           (from, to) => postJSON('/backfill/market-data-fill', { from, to }),
  scrapeBackfill:           (body) => postJSON('/backfill/scrape', body),
  startBackfillRun:         (body) => postJSON('/backfill/run', body),
  listBackfillRuns:         (opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.status) qs.set('status', opts.status);
    if (opts.limit)  qs.set('limit', String(opts.limit));
    const q = qs.toString();
    return fetchJSON(`/backfill/runs${q ? `?${q}` : ''}`);
  },
  getBackfillRun:           (id) => fetchJSON(`/backfill/runs/${id}`),
  runBackfillChecks:        (id, body = {}) => postJSON(`/backfill/runs/${id}/checks`, body),
  getBackfillPromotionPreview: (id) => fetchJSON(`/backfill/runs/${id}/promotion-preview`),
  promoteBackfill:          (body) => postJSON('/backfill/promote', body),
  rollbackBackfill:         (body) => postJSON('/backfill/rollback', body),
  deleteBackfillRun:        (id) => deleteReq(`/backfill/runs/${id}`),

  // ── Engines v2 — workspace-aware, role-gated ──────────────────────────────
  // Each user sees only the workspaces their role grants. Admin can override
  // via ?workspace=retail|pro|trade.
  v2Health:        ()                    => fetchJSON('/v2/health'),
  v2Me:            ()                    => fetchJSON('/v2/me'),
  v2Engines:       (workspace)           => fetchJSON(`/v2/engines${workspace ? `?workspace=${workspace}` : ''}`),
  v2EngineDetail:  (id)                  => fetchJSON(`/v2/engines/${encodeURIComponent(id)}`),
  v2PredictAuto:   (workspace)           => fetchJSON(`/v2/predict${workspace ? `?workspace=${workspace}` : ''}`),
  v2PredictToday:  ()                    => fetchJSON('/v2/predict/today'),
  v2PredictTrade:  (opts = {})           => {
    const qs = new URLSearchParams();
    if (opts.symbol)    qs.set('symbol', opts.symbol);
    if (opts.timeframe) qs.set('timeframe', opts.timeframe);
    const q = qs.toString();
    return fetchJSON(`/v2/predict/trade-signal${q ? `?${q}` : ''}`);
  },
  v2DebugRoster:   ()                    => fetchJSON('/v2/_debug/roster'),
};
